import {
  updateServiceRequest,
  useBusinessEntitiesOfCompany,
  useServiceRequestAssignedBusinessEntities,
} from '@commenda/api';
import {
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  FlagComponent,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToggle,
} from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import { OnboardingStepNotificationEntity } from '@commenda/types/src/company/types';
import { Country } from '@prisma/client';
import { Country as CountryPkg } from 'country-state-city';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { AddEntityModal } from '../../../Corporation/common/BusinessEntity/AddEntityModal';
import { useNotification } from '../../../hooks/useNotification';
import {
  SERVICE_DETAILS_BY_SERVICE_ID,
  COMMENDA_FILING_DETAILS,
} from '../../constants';

interface SelectEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  serviceRequest: Parameters<typeof getServiceRequestServiceId>[0] & {
    isCommendaFiling?: boolean;
  };
  serviceRequestId: string;
  serviceRequestMutate: KeyedMutator<unknown>;
  showInProgressEntities?: boolean;
  serviceRequestTitle: string;
  initialCorporationId?: number;
  hideOtherEntitiesSelection?: boolean;
}

export const SelectEntityModal = ({
  isOpen,
  onClose,
  companyId,
  serviceRequest,
  serviceRequestId,
  serviceRequestMutate,
  showInProgressEntities = false,
  serviceRequestTitle,
  initialCorporationId,
  hideOtherEntitiesSelection = false,
}: SelectEntityModalProps) => {
  const { data: businessEntities, error: businessEntitiesError } =
    useBusinessEntitiesOfCompany(companyId);
  const { data: assignedEntitiesData } =
    useServiceRequestAssignedBusinessEntities(serviceRequestId);
  const { setNotification } = useNotification();

  const [corporationId, setCorporationId] = useState<number | null>(null);
  const [assignedBusinessEntityIds, setAssignedBusinessEntityIds] = useState<
    number[]
  >([]);
  const addEntityModalVisibility = useToggle();

  if (businessEntitiesError) {
    console.error(businessEntitiesError);
  }

  useEffect(() => {
    if (isOpen) {
      setCorporationId(initialCorporationId ?? null);
      setAssignedBusinessEntityIds(
        assignedEntitiesData?.corporations?.map((c) => c.id) ?? []
      );
    }
  }, [
    isOpen,
    initialCorporationId,
    assignedEntitiesData?.corporations,
  ]);

  const serviceCountry = useMemo(() => {
    const serviceId = getServiceRequestServiceId(serviceRequest);
    return (
      (serviceId ? SERVICE_DETAILS_BY_SERVICE_ID[serviceId]?.country : null) ??
      (serviceRequest.isCommendaFiling ? COMMENDA_FILING_DETAILS.country : null)
    );
  }, [serviceRequest]);

  const eligibleEntities = useMemo(() => {
    if (!businessEntities?.entities) return [];

    return businessEntities.entities.filter((entity) => {
      const isInProgress =
        entity.featureSpecificInfo.incorporation.isIncorporationInProgress;
      const matchesCountry = serviceCountry
        ? entity.generalCorporationInfo.incorporationCountry === serviceCountry
        : true;

      if (isInProgress && !showInProgressEntities) {
        return false;
      }

      return matchesCountry;
    });
  }, [businessEntities, serviceCountry, showInProgressEntities]);

  const otherEntities = useMemo(
    () => eligibleEntities.filter((e) => e.id !== corporationId),
    [eligibleEntities, corporationId]
  );

  const otherBusinessEntitiesOptions = useMemo(
    () =>
      otherEntities.map((entity) => ({
        label: entity.generalCorporationInfo.legalName ?? 'Unknown entity',
        value: entity.id.toString(),
      })),
    [otherEntities]
  );

  const handleConfirm = useCallback(async () => {
    if (corporationId == null) return;

    try {
      const response = await updateServiceRequest({
        id: serviceRequestId,
        data: {
          corporationId,
          assignedBusinessEntityIds:
            assignedBusinessEntityIds.length > 0
              ? assignedBusinessEntityIds
              : undefined,
        },
      });

      if (!response.ok) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Failed to link entity to service request. Please try again.',
        });
        return;
      }

      await serviceRequestMutate();
      onClose();
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Entity linked successfully.',
      });
    } catch (error) {
      console.error('Failed to link entity:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Failed to link entity to service request. Please try again.',
      });
    }
  }, [
    corporationId,
    assignedBusinessEntityIds,
    serviceRequestId,
    serviceRequestMutate,
    onClose,
    setNotification,
  ]);

  const handlePrimaryChange = useCallback((value: string) => {
    setCorporationId(+value);
    setAssignedBusinessEntityIds([]);
  }, []);

  const handleAddEntitySuccess = useCallback(
    async (corporation: OnboardingStepNotificationEntity) => {
      if (corporation.id != null) {
        setCorporationId(corporation.id);
      }
      addEntityModalVisibility.hide();
    },
    [addEntityModalVisibility]
  );

  const serviceDisplayName = serviceRequestTitle || 'N/A';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="min-w-[800px]" hideCloseIcon>
          <DialogHeader className="flex flex-col justify-start items-start gap-md px-4 py-4 border-b-0">
            <DialogTitle className="max-w-[90%]">
              {serviceDisplayName}
            </DialogTitle>
            <DialogDescription>
              <span className="text-sm font-normal text-text-tertiary">
                Please select an entity to link to this service request.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="gap-2xl px-4 flex flex-col pb-4">
            {eligibleEntities.length > 0 ? (
              <div className="flex flex-col gap-lg">
                <FieldWrapper>
                  <Label htmlFor="corporationId">Primary Corporation</Label>
                  <Select
                    value={corporationId?.toString() ?? ''}
                    onValueChange={handlePrimaryChange}
                  >
                    <SelectTrigger id="corporationId">
                      <SelectValue placeholder="Select a corporation" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleEntities.map((entity) => (
                        <SelectItem
                          key={entity.id}
                          value={entity.id.toString()}
                        >
                          {entity.generalCorporationInfo.legalName ??
                            'Unknown Corporation'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldWrapper>

                {!hideOtherEntitiesSelection && (
                  <FieldWrapper>
                    <Label>Choose additional business entities</Label>
                    <MultiSelect
                      options={otherBusinessEntitiesOptions}
                      onValueChange={(value) =>
                        setAssignedBusinessEntityIds(value.map((v) => +v))
                      }
                      defaultValue={assignedBusinessEntityIds.map((v) =>
                        v.toString()
                      )}
                    />
                  </FieldWrapper>)}
              </div>
            ) : (
              <div className="flex flex-col gap-md pb-5">
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={addEntityModalVisibility.show}
                >
                  Add entity
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="p-5 flex items-center justify-end">
            <div className="flex gap-lg">
              <Button variant="secondary" onClick={onClose}>
                Back
              </Button>
              <Button
                disabled={!corporationId}
                onClick={handleConfirm}
                variant="primary"
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AddEntityModal
        isOpen={addEntityModalVisibility.visible}
        onClose={addEntityModalVisibility.hide}
        companyId={companyId}
        defaultValues={{
          incorporationCountry: serviceCountry as Country,
        }}
        onSuccess={handleAddEntitySuccess}
      />
    </>
  );
};
